import styled from "styled-components";
import { useEffect, useState } from "react";
import useScreenSize from "../../../hooks/useScreenSize";
import { Menu } from "lucide-react";

const WorkshopPageContent = ({ sections }) => {
    const [activeSection, setActiveSection] = useState(sections[0]?.id);
    const [showMobileNav, setIsMobileNav] = useState(false);
    const { width } = useScreenSize();

    useEffect(() => {
        const handleScroll = () => {
            const sectionElements = sections.map(s => ({
                id: s.id,
                element: document.getElementById(s.id),
            }));

            const scrollPosition = window.scrollY + 150;

            for (let i = sectionElements.length - 1; i >= 0; i--) {
                const section = sectionElements[i];
                if (section.element && section.element.offsetTop <= scrollPosition) {
                    setActiveSection(section.id);
                    break;
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [sections]);

    useEffect(() => {
        const buttons = document.getElementsByClassName("side-bar-menu");
        if (buttons.length > 0) {
            buttons[0].style.display = width < 850 ? "block" : "none";
        }
        if (width >= 850) setIsMobileNav(false);
    }, [width]);

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            window.scrollTo({ top: element.offsetTop - 100, behavior: 'smooth' });
        }
    };

    return (
        <Wrapper>
            <Container>
                <Navigation>
                    <button className="side-bar-menu" onClick={() => setIsMobileNav(prev => !prev)}>
                        <Menu size={16} />
                    </button>
                    <NavCard $showMobile={showMobileNav}>
                        <NavTitle>Navigation</NavTitle>
                        <NavList>
                            {sections.map((section) => {
                                const Icon = section.icon;
                                return (
                                    <li key={section.id}>
                                        <NavButton
                                            onClick={() => {
                                                scrollToSection(section.id);
                                                if (width < 850) setIsMobileNav(false);
                                            }}
                                            $active={activeSection === section.id}
                                        >
                                            <Icon size={16} />
                                            <span>{section.label}</span>
                                        </NavButton>
                                    </li>
                                );
                            })}
                        </NavList>
                    </NavCard>
                </Navigation>

                <ContentArea>
                    {sections.map((section) => {
                        const Icon = section.icon;
                        const SectionWrapper = section.variant === 'highlight' ? ApplicationSection : Section;
                        return (
                            <SectionWrapper id={section.id} key={section.id}>
                                <SectionHeader $onDark={section.variant === 'highlight'}>
                                    <Icon size={32} color={section.variant === 'highlight' ? '#ffffff' : '#0c4e80ff'} />
                                    {section.label}
                                </SectionHeader>
                                <SectionContent>
                                    {section.content}
                                </SectionContent>
                            </SectionWrapper>
                        );
                    })}
                </ContentArea>
            </Container>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    font-family: "PT Serif", serif;
    font-weight: 200;
    font-size: 1rem;
`;

const Container = styled.div`
    max-width: 72rem;
    margin: 0 auto;
    padding: 3rem 1.5rem;
    display: flex;
    align-items: flex-start;
    gap: 2rem;
    width: 100%;
    box-sizing: border-box;
    overflow: visible;

    @media only screen and (max-width: 850px) {
        display: block;
        padding: 2rem 1rem;
        gap: 0;
    }
`;

const ContentArea = styled.main`
    flex: 1;
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
`;

const Section = styled.section`
    margin-bottom: 4rem;
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    padding: 2rem;
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;

    @media (max-width: 850px) {
        padding: 1.25rem;
        margin-bottom: 2rem;
        border-radius: 0.5rem;
    }
`;

const ApplicationSection = styled(Section)`
    background-color: #0c4e80ff;
    color: white;
`;

const SectionHeader = styled.h2`
    font-size: 1.875rem;
    font-weight: bold;
    color: ${props => (props.$onDark ? '#ffffff' : '#1f2937')};
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    word-break: break-word;

    @media (max-width: 850px) {
        font-size: 1.35rem;
        svg { width: 24px; height: 24px; flex-shrink: 0; }
    }
`;

const SectionContent = styled.div`
    color: #374151;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
`;

const Navigation = styled.div`
    width: 16rem;
    flex-shrink: 0;
    position: sticky;
    top: 5rem;
    height: fit-content;
    z-index: 2;
    align-self: flex-start;

    .side-bar-menu {
        display: none;
        background: #0c4e80ff;
        color: white;
        border: none;
        padding: 0.75rem 1rem;
        border-radius: 0.5rem;
        cursor: pointer;
        margin-bottom: 1rem;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

        &:hover { background: #082D4A; }
    }

    @media only screen and (max-width: 850px) {
        width: 100%;
        position: relative;
        top: 0;
        margin-bottom: 1.5rem;
        align-self: auto;
    }
`;

const NavCard = styled.div`
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    padding: 0.5rem 1.5rem;
    box-sizing: border-box;

    @media only screen and (max-width: 850px) {
        display: ${props => (props.$showMobile ? 'block' : 'none')};
        position: absolute;
        z-index: 10;
        max-height: 80vh;
        overflow-y: auto;
        left: 0;
        right: 0;
        margin: 0 1rem;
        width: calc(100% - 2rem);
    }
`;

const NavTitle = styled.h2`
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 1rem;
`;

const NavList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const NavButton = styled.button`
    width: 100%;
    text-align: left;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.875rem;
    font-weight: 500;
    transition: all 0.2s;
    background: ${props => (props.$active ? '#CA9D3B' : 'transparent')};
    color: ${props => (props.$active ? 'white' : '#374151')};
    box-shadow: ${props => (props.$active ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none')};

    &:hover {
        background: ${props => (props.$active ? '#CA9D3B' : '#eff6ff')};
    }
`;


export const GeneralInfoText = styled.p`
    font-size: 1rem;
    line-height: 1.8;
    color: #374151;
    margin-bottom: 0.3rem;
    text-align: justify;

    strong { color: #0c4e80ff; font-weight: 300; }

    @media (max-width: 850px) {
        font-size: 0.95rem;
        text-align: left;
    }
`;

export const CollaborationHighlight = styled.div`
    background: linear-gradient(135deg, #0c4e80ff 0%, #082D4A 100%);
    border-radius: 0.75rem;
    padding: 1.5rem;
    display: flex;
    gap: 1rem;
    align-items: start;
    margin: 1.5rem 0;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    @media (max-width: 850px) {
        flex-direction: column;
        padding: 1.25rem;
    }
`;

export const HighlightIcon = styled.div`
    background: white;
    border-radius: 50%;
    padding: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

export const HighlightText = styled.p`
    color: white;
    font-size: 0.9rem;
    line-height: 1.7;
    margin: 0;
    font-style: italic;

    @media (max-width: 850px) { font-size: 0.95rem; }
`;

export const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;

    @media (max-width: 850px) { grid-template-columns: 1fr; }
`;

export const NoticeCard = styled.div`
    background: #ffffff;
    margin-top: 1rem;
    border: 1px solid #e5e7eb;
    border-top: 7px solid #0c4e80;
    border-radius: 12px;
    padding: 1.25rem 1.25rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: all 0.3s ease-in-out;

    &:hover {
        transform: translateY(-5px);
    }
`;

export const CardIconWrap = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #e6f1fb;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const InfoBoxTitle = styled.h3`
    font-weight: 600;
    font-size: 1.125rem;
    color: #0c4e80;
    margin: 0.5rem 0 0.25rem;
    word-break: break-word;

    @media (max-width: 850px) {
        font-size: 1rem;
    }
`;

export const InfoBoxContent = styled.h4`
    margin-top: 0;
    margin-bottom: 2rem;
    word-break: break-word;
    color: #0c4e80;

    @media (max-width: 850px) { font-size: 1rem; }
`;

export const PillRow = styled.div`
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
`;

export const Pill = styled.span`
    font-size: 11px;
    font-weight: 500;
    padding: 3px 10px;
    border-radius: 999px;
    background: ${p => (p.$gold ? '#FAEEDA' : '#E6F1FB')};
    color: ${p => (p.$gold ? '#633806' : '#0C447C')};
    border: 0.5px solid ${p => (p.$gold ? '#FAC775' : '#B5D4F4')};
`;

export const FeeAmount = styled.div`
    font-size: 26px;
    font-weight: 600;
    color: #0c4e80;
    line-height: 1.1;
`;

export const FeeGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 14px;
    margin-top: 0.25rem;

    @media (max-width: 500px) {
        grid-template-columns: 1fr;
    }
`;

export const FeeItem = styled.div`
    padding: 10px 12px;
    background: #f8fbfe;
    border: 1px solid #e6eef5;
    border-radius: 8px;
`;

export const FeeLabel = styled.div`
    font-size: 9px;
    line-height: 1.3;
    color: #6b7280;
    margin-bottom: 4px;
`;

export const FeeValue = styled.div`
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
    color: #0c4e80;
`;

export const TextSub = styled.div`
    font-size: 11px;
    line-height: 1.4;
    color: #9ca3af;
    margin-top: 2px;

    a:hover {
        text-decoration: underline;
    }
`;

export const ApplicationBox = styled.div`
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 0.5rem;
    padding: 1.5rem;
    color: white;
    box-sizing: border-box;
    width: 100%;
    overflow-wrap: break-word;

    @media (max-width: 850px) { padding: 1rem; }
`;

export const ApplicationTitle = styled.h3`
    font-weight: 600;
    font-size: 1.25rem;
    margin-bottom: 0.75rem;
`;

export const OrderedList = styled.ol`
    list-style: none;
    padding: 0;
    margin-left: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

export const OrderedListItem = styled.li`
    display: flex;
    gap: 0.75rem;
`;

export const StepNumber = styled.span`
    font-weight: bold;
`;

export const ButtonContainer = styled.div`
    text-align: center;
    padding-top: 1rem;
`;

export const ApplyButton = styled.button`
    background: white;
    color: #2563eb;
    padding: 1rem 2rem;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 1.125rem;
    border: none;
    cursor: pointer;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    transition: background 0.2s;

    &:hover { background: #eff6ff; }
`;

export default WorkshopPageContent;